import React from 'react';

import { gql, useMutation } from '@apollo/client';

import type { FetchResult, MutationHookOptions } from '@apollo/client';
import type { JobHiringAttendance } from '../type';

type RejectedAttendance = Pick<
  JobHiringAttendance,
  'id' | 'status' | 'startOperationTime' | 'endOperationTime' | 'breakDurationMinute'
> & {
  jobHiring?: {
    id: string;
    status: string;
    startOperationTime: string;
    endOperationTime: string;
    breakDurationMinute: number;
    operationDurationMinute: number;
  } | null;
};

type RejectAttendancesData = {
  rejectJobHiringAttendances: {
    success: boolean;
    errors?: string[][] | null;
    data?: RejectedAttendance[] | null;
  };
};

export type RejectAttendanceInput = { id: string };

type RejectAttendancesVariables = { attendances: RejectAttendanceInput[] };

export type RejectAttendancesResult = {
  success: boolean;
  data?: RejectedAttendance[] | null;
  errors?: string[][] | null;
  error?: Error | unknown | null;
  result?: FetchResult<RejectAttendancesData> | null;
};

const rejectAttendancesGql = gql(`
  mutation rejectJobHiringAttendances($attendances: [RejectAttendanceInput!]!) {
    rejectJobHiringAttendances(attendances: $attendances) {
      success
      errors
      data {
        id
        status
        startOperationTime
        endOperationTime
        breakDurationMinute
        jobHiring {
          id
          status
          startOperationTime
          endOperationTime
          breakDurationMinute
          operationDurationMinute
        }
      }
    }
  }
`);

const useRejectAttendancesMutation = (
  options?: MutationHookOptions<RejectAttendancesData, RejectAttendancesVariables>,
) => {
  const [rejectAttendancesMutation, mutationResult] = useMutation<RejectAttendancesData, RejectAttendancesVariables>(
    rejectAttendancesGql,
    options,
  );

  const rejectAttendances = React.useCallback(
    async (attendances: RejectAttendanceInput[]): Promise<RejectAttendancesResult> => {
      try {
        const result = await rejectAttendancesMutation({
          variables: { attendances },
        });

        return {
          success: !!result.data?.rejectJobHiringAttendances.success,
          data: result.data?.rejectJobHiringAttendances.data ?? null,
          errors: result.data?.rejectJobHiringAttendances.errors ?? null,
          result,
        };
      } catch (error) {
        return {
          success: false,
          error,
        };
      }
    },
    [rejectAttendancesMutation],
  );

  return {
    rejectAttendances,
    mutate: rejectAttendancesMutation,
    result: mutationResult,
  };
};

export default useRejectAttendancesMutation;
